<template>
  <DialogHeader>
    <template #right>
      <div
        v-if="!loading"
        class="font-medium text-green-900 cursor-pointer"
        @click="handleSubmit"
      >
        Save
      </div>
      <Icon
        v-else
        name="Loading"
        :size="14"
        class="stroke-gray-800 animate-spin"
      />
    </template>
    Change password
  </DialogHeader>
  <DialogContent>
    <div class="mb-5">
      <UiInput
        v-model="currentPassword"
        label="Current password"
        type="password"
        :disabled="loading"
        :errorMsg="currentPasswordError"
      />
    </div>
    <div class="mb-5">
      <UiInput
        v-model="newPassword"
        label="New password"
        type="password"
        :disabled="loading"
      />
    </div>
    <UiInput
      v-model="confirmPassword"
      label="Confirm new password"
      type="password"
      :disabled="loading"
      :errorMsg="confirmPasswordError"
    />
  </DialogContent>
  <DialogFooter>
    <div class="w-full flex items-center justify-between">
      <div
        class="text-link font-primary text-[14px] leading-[16px]"
        :class="linkClasses"
        @click="handleForgotPasswordClick"
      >
        Forgot password?
      </div>
      <div class="flex">
        <UiButton
          class="w-1/2 tablet-h:w-auto mr-2.5 tablet-h:mr-2"
          @click="close()"
        >
          Cancel
        </UiButton>
        <UiButton
          class="w-1/2 tablet-h:w-auto"
          primary
          :disabled="disableButton"
          :loading="loading"
          @click="handleSubmit"
        >
          Change password
        </UiButton>
      </div>
    </div>
  </DialogFooter>
</template>

<script setup lang="ts">
import { UiButton, UiInput, Icon } from '@dacr/ui'
import DialogHeader from '@/shared/dialogs/DialogHeader.vue'
import DialogContent from '@/shared/dialogs/DialogContent.vue'
import DialogFooter from '@/shared/dialogs/DialogFooter.vue'
import { useDialog } from '@/shared/dialogs/useDialog'
import { useChangePasswordMutation } from '@/shared/graphql/legacy/generated'
import { ToastType, useToastStore } from '@/shared/stores/toast'
import { NativeActionsEnum, useWebviewStore } from '@/shared/stores/webview'
import { computed, ref, watch } from 'vue'
import { useRouter } from 'vue-router'

const { close } = useDialog()
const toastStore = useToastStore()
const webviewStore = useWebviewStore()
const router = useRouter()

const currentPassword = ref('')
const currentPasswordError = ref('')
const newPassword = ref('')
const confirmPassword = ref('')
const confirmPasswordError = ref('')

const { mutate: changePassword, loading } = useChangePasswordMutation()

const disableButton = computed(
  () =>
    !currentPassword.value.trim() ||
    !newPassword.value.trim() ||
    !confirmPassword.value.trim()
)

const linkClasses = computed(() => ({
  'pointer-events-none': loading.value,
}))

const handleSubmit = async () => {
  try {
    if (newPassword.value !== confirmPassword.value) {
      confirmPasswordError.value = 'Passwords do not match.'
      return
    }

    await changePassword({
      oldPassword: currentPassword.value,
      newPassword: newPassword.value,
    })

    toastStore.showToast({
      type: ToastType.Success,
      content: 'Password changed successfully',
    })

    close()
  } catch (error) {
    if (error instanceof Error && error.message.includes('incorrect')) {
      currentPasswordError.value = 'Password is incorrect.'
    }
  }
}

watch(currentPassword, () => {
  currentPasswordError.value = ''
})

watch([newPassword, confirmPassword], () => {
  confirmPasswordError.value = ''
})

const handleForgotPasswordClick = async () => {
  if (webviewStore.isActive) {
    webviewStore.postMessage({
      type: NativeActionsEnum.OpenForgotPassword,
    })
  } else {
    await router.push('/forgot-password')
  }

  close()
}
</script>
